/**
 * Plan Restrictions Utility
 * Handles checking user subscription plans and feature access
 */

// Plan hierarchy and feature access - Enterprise restrictions as requested by user
const PLAN_FEATURES = {
  'Starter': {
    ai_chat: false,
    heatmaps: false,
    api_access: false,
    websites: 1,
    events_per_month: 10000,
    users: 1,
    data_retention_days: 7
  },
  'Growth': {
    ai_chat: false,
    heatmaps: false,
    api_access: false,
    live_experience_score: true, // Real-time UX health monitoring
    websites: 3,
    events_per_month: 100000,
    users: 3,
    data_retention_days: 180 // 6 months
  },
  'Enterprise': {
    ai_chat: true, // AI agent with continuous monitoring
    heatmaps: true, // Unlimited heatmaps + session replay
    api_access: true,
    live_experience_score: true, // Real-time UX health monitoring
    websites: 10,
    events_per_month: 1000000,
    users: 10,
    data_retention_days: 365 // 12 months
  },
};

const normalizePlanName = (planName = '') => {
  const name = String(planName).trim().toLowerCase();
  if (!name) return null;
  if (name.includes('enterprise')) return 'Enterprise';
  if (name.includes('growth')) return 'Growth';
  if (name.includes('starter')) return 'Starter';
  return planName;
};

/**
 * Check if user has access to a specific feature
 * @param {Object} currentPlan - Current user plan object
 * @param {string} feature - Feature to check (ai_chat, heatmaps, api_access)
 * @returns {boolean} Whether user has access to the feature
 */
export const hasFeatureAccess = (currentPlan, feature) => {
  if (!currentPlan || !currentPlan.plan_name) {
    return false; // No plan = no access
  }

  const planName = currentPlan.plan_name;
  const normalizedPlanName = normalizePlanName(planName);
  let planFeatures = normalizedPlanName ? PLAN_FEATURES[normalizedPlanName] : PLAN_FEATURES[planName];
  
  if (!planFeatures) {
    const featureFlag = currentPlan?.features?.[feature];
    if (typeof featureFlag === 'boolean') return featureFlag;
    if (typeof featureFlag === 'string') return featureFlag.toLowerCase() === 'true';
    if (typeof featureFlag === 'number') return featureFlag > 0;
    return false; // Unknown plan = no access
  }

  return planFeatures[feature] === true;
};

/**
 * Check if user is on Enterprise plan or higher
 * @param {Object} currentPlan - Current user plan object
 * @returns {boolean} Whether user has enterprise access
 */
export const hasEnterpriseAccess = (currentPlan) => {
  if (!currentPlan || !currentPlan.plan_name) {
    return false;
  }

  const planName = normalizePlanName(currentPlan.plan_name);
  if (!planName) return false;
  return planName === 'Enterprise' || planName === 'White Label';
};

/**
 * Get upgrade message for a specific feature
 * @param {string} feature - Feature name
 * @returns {Object} Message object with title and description
 */
export const getUpgradeMessage = (feature) => {
  const messages = {
    ai_chat: {
      title: 'AI Chat Requires Growth or Enterprise Plan',
      description: 'Get intelligent analytics insights and recommendations with our AI assistant. Upgrade to Enterprise to unlock this powerful feature.'
    },
    heatmaps: {
      title: 'Heatmaps Require Growth or Enterprise Plan',
      description: 'Visualize user interactions with advanced heatmap analytics including session replay. Upgrade to Enterprise plan to access heatmaps.'
    },
    api_access: {
      title: 'API Access Requires Enterprise Plan',
      description: 'Integrate your analytics data with external systems using our REST API. Upgrade to Enterprise plan for API access.'
    }
  };

  return messages[feature] || {
    title: 'Feature Requires Plan Upgrade',
    description: 'This feature is available with higher tier plans. Please upgrade to access this functionality.'
  };
};

/**
 * Get recommended plans for a feature
 * @param {string} feature - Feature name
 * @returns {Array} Array of plan names that support the feature
 */
export const getRecommendedPlans = (feature) => {
  const recommendedPlans = [];
  
  Object.entries(PLAN_FEATURES).forEach(([planName, features]) => {
    if (features[feature] === true) {
      recommendedPlans.push(planName);
    }
  });
  
  return recommendedPlans;
};

/**
 * Check if current plan supports a specific limit
 * @param {Object} currentPlan - Current user plan object
 * @param {string} limitType - Type of limit (websites, events_per_month, users)
 * @param {number} currentUsage - Current usage amount
 * @returns {Object} Object with hasAccess boolean and limit info
 */
export const checkPlanLimit = (currentPlan, limitType, currentUsage = 0) => {
  if (!currentPlan || !currentPlan.plan_name) {
    return { hasAccess: false, limit: 0, usage: currentUsage };
  }

  const planName = normalizePlanName(currentPlan.plan_name) || currentPlan.plan_name;
  const planFeatures = PLAN_FEATURES[planName];
  
  if (!planFeatures) {
    return { hasAccess: false, limit: 0, usage: currentUsage };
  }

  const limit = planFeatures[limitType];
  
  // -1 means unlimited
  if (limit === -1) {
    return { hasAccess: true, limit: -1, usage: currentUsage, unlimited: true };
  }

  return {
    hasAccess: currentUsage < limit,
    limit: limit,
    usage: currentUsage,
    unlimited: false
  };
};

const planRestrictions = {
  hasFeatureAccess,
  hasEnterpriseAccess,
  getUpgradeMessage,
  getRecommendedPlans,
  checkPlanLimit,
  PLAN_FEATURES
};

export default planRestrictions;
